import { Fragment } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styles from './ScienceBreadcrumb.module.css';

/* Trailing filler is stripped after a slug is shortened so the crumb does not
   end mid-preposition ("…altitude-on"). */
const TRAILING_FILLER = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'between', 'by', 'do', 'does',
  'for', 'from', 'in', 'into', 'is', 'it', 'its', 'more', 'of', 'on', 'or',
  'the', 'than', 'that', 'to', 'with',
]);

/* Slugs at or under this length are shown whole. */
const SLUG_KEEP_WHOLE = 6;
/* Longer ones are cut to this many words, before trailing filler is trimmed. */
const SLUG_TARGET_WORDS = 5;

export const shortSlugLabel = (slug: string): string => {
  const words = slug.split('-').filter(Boolean);
  if (words.length <= SLUG_KEEP_WHOLE) return words.join('-');

  const trimmed = words.slice(0, SLUG_TARGET_WORDS);
  while (trimmed.length > 2 && TRAILING_FILLER.has(trimmed[trimmed.length - 1])) {
    trimmed.pop();
  }
  return trimmed.join('-');
};

/* href-less: /science 307s to the studies hub and has no client landing.
   The label must stay in sync with science.common.rootLabel — it is the value
   the render compares against to decide which crumb gets translated. */
export const SCIENCE_ROOT_CRUMB: Crumb = { label: 'Science' };

export interface Crumb {
  label: string;
  href?: string;
}

interface Props {
  crumbs: Crumb[];
  dark?: boolean;
}

const ScienceBreadcrumb = ({ crumbs, dark = false }: Props) => {
  const t = useTranslations('science');
  if (crumbs.length === 0) return null;

  return (
    <nav
      className={styles.crumbs}
      data-dark={dark}
      aria-label={t('science.common.breadcrumb')}
    >
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        const label =
          c.label === SCIENCE_ROOT_CRUMB.label
            ? t('science.common.rootLabel')
            : c.label;
        return (
          <Fragment key={`${c.label}-${i}`}>
            {i > 0 && (
              <span className={styles.sep} aria-hidden="true">
                /
              </span>
            )}
            {c.href && !isLast ? (
              <Link className={styles.crumbLink} href={c.href}>
                {label}
              </Link>
            ) : isLast ? (
              <span className={styles.crumbCurrent} aria-current="page">
                {label}
              </span>
            ) : (
              <span className={styles.crumbInert}>{label}</span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
};

export default ScienceBreadcrumb;
